<template>
  <div class="monaco-editor-container">
    <div class="monaco-editor-toolbar" v-if="showToolbar">
      <button
        @click="formatDocument"
        title="Format Document"
        class="toolbar-button"
        :disabled="!editor || loading"
      >
        <Icon name="symbol-misc" />
        Format
      </button>
      <button
        @click="minifyDocument"
        title="Minify Document"
        class="toolbar-button"
        :disabled="!editor || loading"
      >
        <Icon name="fold" />
        Minify
      </button>
      <span class="language-info">{{ actualLanguage }}</span>
    </div>
    
    <div 
      ref="editorRef" 
      class="monaco-editor"
      :class="{
        'monaco-editor--loading': loading,
        'monaco-editor--readonly': readonly
      }"
    />
    
    <div v-if="loading" class="monaco-editor-loading">
      Loading editor...
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick, computed, markRaw } from 'vue'
import Icon from '../atoms/Icon.vue'
import { 
  monaco,
  initializeMonaco
} from '@/utils/monaco-setup'

interface Props {
  modelValue: string
  language?: string
  filename?: string
  theme?: 'vs' | 'vs-dark' | 'hc-black' | 'hc-light' | 'auto'
  readonly?: boolean
  showToolbar?: boolean
  height?: string | number
  fontSize?: number
  tabSize?: number
  wordWrap?: 'off' | 'on' | 'wordWrapColumn' | 'bounded'
}

interface Emits {
  (e: 'update:modelValue', value: string): void
  (e: 'theme-changed', theme: string): void
}

const props = withDefaults(defineProps<Props>(), {
  language: 'json',
  theme: 'vs-dark',
  readonly: false,
  showToolbar: true,
  height: '400px',
  fontSize: 14,
  tabSize: 2,
  wordWrap: 'off'
})

const emit = defineEmits<Emits>()

// Refs
const editorRef = ref<HTMLElement>()
const editor = ref<monaco.editor.IStandaloneCodeEditor>()
const loading = ref(true)
const editorValue = ref(props.modelValue) // Track editor content without reactivity issues

// Computed
const actualLanguage = computed(() => {
  // If language is explicitly provided, use it
  if (props.language) return props.language
  
  // If filename is provided, try filename-based detection as fallback
  if (props.filename) return getLanguageFromFilename(props.filename)
  
  // Use content-based detection
  return detectLanguageFromContent(props.modelValue)
})

const editorHeight = computed(() => {
  // if (typeof props.height === 'number') {
  //   return `${props.height}px`
  // }
  // return props.height || '100%'
  return '100%'
})

// Content-based language detection
function detectLanguageFromContent(content: string): string {
  if (!content.trim()) return 'plaintext'
  
  const trimmedContent = content.trim()
  
  // JSON detection
  if (trimmedContent.startsWith('{') || trimmedContent.startsWith('[')) {
    try {
      JSON.parse(content)
      return 'json'
    } catch {
      // Could be JSONC or malformed JSON
      if (content.includes('//') || content.includes('/*')) {
        return 'jsonc'
      }
      // Still treat as JSON for syntax highlighting
      return 'json'
    }
  }
  
  // XML detection
  if (trimmedContent.startsWith('<?xml') || 
      (trimmedContent.startsWith('<') && trimmedContent.includes('</'))) {
    return 'xml'
  }
  
  // JavaScript detection
  if (content.includes('function ') || 
      content.includes('const ') || 
      content.includes('let ') ||
      content.includes('var ') ||
      content.includes('class ') || 
      content.includes('=>') ||
      content.includes('import ') ||
      content.includes('export ')) {
    return 'javascript'
  }
  
  // CSS detection
  if (content.includes('{') && 
      content.includes('}') && 
      content.includes(':') && 
      content.includes(';') &&
      !content.includes('function') &&
      !content.includes('class') &&
      !content.includes('const')) {
    return 'css'
  }
  
  // TypeScript detection
  if (content.includes('interface ') || 
      content.includes('type ') || 
      content.includes(': string') || 
      content.includes(': number') ||
      content.includes(': boolean') ||
      content.includes('<T>') ||
      content.includes('enum ')) {
    return 'typescript'
  }
  
  // HTML detection
  if (trimmedContent.startsWith('<!DOCTYPE') ||
      trimmedContent.startsWith('<html') ||
      (content.includes('<') && content.includes('>') && content.includes('</'))) {
    return 'html'
  }
  
  return 'plaintext'
}

// Helper to get language from filename (fallback)
function getLanguageFromFilename(filename: string): string {
  const ext = filename.toLowerCase().split('.').pop()
  switch (ext) {
    case 'json': return 'json'
    case 'jsonc': return 'jsonc'
    case 'xml': return 'xml'
    case 'js': return 'javascript'
    case 'ts': return 'typescript'
    case 'css': return 'css'
    case 'html': return 'html'
    case 'htm': return 'html'
    default: return 'plaintext'
  }
}

// Initialize Monaco Editor
async function initEditor() {
  if (!editorRef.value) return
  
  try {
    loading.value = true
    
    // Initialize Monaco asynchronously to prevent main thread blocking
    await new Promise(resolve => {
      requestAnimationFrame(async () => {
        try {
          await initializeMonaco()
          resolve(void 0)
        } catch (error) {
          console.error('Monaco initialization error:', error)
          resolve(void 0)
        }
      })
    })
    
    // Create editor with double-RAF to ensure complete non-blocking behavior
    await new Promise(resolve => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          try {
            const editorInstance = monaco.editor.create(editorRef.value!, {
              value: props.modelValue,
              language: actualLanguage.value,
              theme: props.theme === 'auto' ? 'vs-dark' : (props.theme === 'hc-light' ? 'vs' : props.theme),
              readOnly: props.readonly,
              fontSize: props.fontSize,
              tabSize: props.tabSize,
              insertSpaces: true,
              automaticLayout: true,
              scrollBeyondLastLine: false,
              minimap: { enabled: false },
              wordWrap: props.wordWrap,
              lineNumbers: 'on',
              folding: true,
              quickSuggestions: true,
              suggest: {
                showKeywords: true,
                showSnippets: true,
                showFunctions: true,
                showMethods: true,
                showVariables: true,
                showClasses: true,
                showFields: true,
                showEnums: true,
                showEvents: true,
                showInterfaces: true,
                showModules: true,
                showProperties: true,
                showUnits: true,
                showValues: true,
                showConstants: true,
                showReferences: true,
                showTypeParameters: true
              },
              colorDecorators: true,
              'semanticHighlighting.enabled': true
            })
            
            editor.value = markRaw(editorInstance) // Prevent Monaco from becoming reactive
            
            // Debug logging for language and features
            console.log('Editor created with language:', actualLanguage.value)
            console.log('Available languages:', monaco.languages.getLanguages().map(l => l.id))
            console.log('Model language:', editorInstance.getModel()?.getLanguageId())
            
            // Verify language services are working
            setTimeout(() => {
              const model = editorInstance.getModel()
              if (model) {
                console.log('Model language after initialization:', model.getLanguageId())
                console.log('Model URI:', model.uri.toString())
              }
            }, 1000)
            
            // Register custom minify action with Monaco
            editorInstance.addAction({
              id: 'minify-document',
              label: 'Minify Document',
              keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyM],
              contextMenuGroupId: 'navigation',
              contextMenuOrder: 1.6,
              run: async () => {
                await minifyDocument()
              }
            })
            
            // Listen for content changes with debouncing to prevent main thread blocking
            let changeTimeout: number | undefined
            const changeHandler = () => {
              clearTimeout(changeTimeout)
              changeTimeout = setTimeout(() => {
                const value = editorInstance.getValue()
                editorValue.value = value
                emit('update:modelValue', value)
              }, 100) // 100ms debounce
            }
            
            editorInstance.onDidChangeModelContent(changeHandler)
            
            // Store cleanup function
            ;(editorInstance as any)._cleanupChangeTimeout = () => {
              clearTimeout(changeTimeout)
            }
            
            resolve(void 0)
          } catch (error) {
            console.error('Editor creation error:', error)
            resolve(void 0)
          }
        })
      })
    })
    
    loading.value = false
  } catch (error) {
    console.error('Failed to initialize Monaco Editor:', error)
    loading.value = false
  }
}

// Format document
async function formatDocument() {
  if (!editor.value) return
  
  try {
    // Use requestAnimationFrame to prevent blocking
    await new Promise(resolve => {
      requestAnimationFrame(async () => {
        try {
          const model = editor.value?.getModel()
          const language = model?.getLanguageId()
          const beforeValue = model?.getValue() || ''
          console.groupCollapsed('[MonacoEditor] Format Document')
          console.log('Language:', language)
          console.log('Before length:', beforeValue.length)
          
          // Capture indentation stats pre-format
          const beforeLines = beforeValue.split(/\n/)
          const beforeAvgIndent = beforeLines.reduce((acc,l)=>acc+(l.match(/^\s*/)?.[0].length||0),0)/Math.max(1,beforeLines.length)
          console.log('Average indent (chars) before:', beforeAvgIndent.toFixed(2))
          
          const actions = editor.value?.getSupportedActions() || []
          console.log('Supported format actions:', actions.filter(a => a.id.includes('format')).map(a => ({id:a.id,label:a.label,supported:a.isSupported()})))
          const action = editor.value?.getAction('editor.action.formatDocument')
          let usedBuiltIn = false
          if (action && action.isSupported()) {
            try {
              await action.run()
              usedBuiltIn = true
              console.log('Built-in formatter executed.')
            } catch (err) {
              console.warn('Built-in formatter threw error, will fallback:', err)
            }
          } else {
            console.warn('Built-in format action not available or unsupported.')
          }
          
          // After attempt
          const afterValue = model?.getValue() || ''
          const afterLines = afterValue.split(/\n/)
          const afterAvgIndent = afterLines.reduce((acc,l)=>acc+(l.match(/^\s*/)?.[0].length||0),0)/Math.max(1,afterLines.length)
          console.log('After length:', afterValue.length)
          console.log('Average indent (chars) after built-in:', afterAvgIndent.toFixed(2))
          
          const looksMinifiedJson = language?.startsWith('json') && /[{\[]/.test(afterValue) && afterValue.indexOf('\n') === -1
          const noIndentIncrease = afterAvgIndent <= 0.1 && beforeAvgIndent <= 0.1
          if (language?.startsWith('json') && (!usedBuiltIn || looksMinifiedJson || noIndentIncrease)) {
            try {
              // Manual pretty-print fallback
              const parsed = JSON.parse(afterValue)
              const pretty = JSON.stringify(parsed, null, 2)
              if (pretty !== afterValue) {
                const fullRange = model!.getFullModelRange()
                model!.pushEditOperations([], [{ range: fullRange, text: pretty, forceMoveMarkers: true }], () => null)
                editorValue.value = pretty
                emit('update:modelValue', pretty)
                console.log('Applied manual JSON pretty-print fallback (2-space indent).')
              } else {
                console.log('Manual formatter produced same output; no edit applied.')
              }
            } catch (jsonErr) {
              console.warn('Manual JSON pretty-print failed (likely invalid JSON):', jsonErr)
            }
          }
          console.groupEnd()
        } catch (error) {
          console.error('Format document error:', error)
        }
        resolve(void 0)
      })
    })
  } catch (error) {
    console.error('Format document failed:', error)
  }
}

// Minification helper functions
function minifyJson(content: string): string {
  try {
    const parsed = JSON.parse(content)
    return JSON.stringify(parsed)
  } catch (error) {
    console.warn('Invalid JSON for minification:', error)
    return content
  }
}

function minifyXml(content: string): string {
  try {
    // Remove whitespace between tags while preserving content
    return content
      .replace(/>\s+</g, '><')
      .replace(/\s+/g, ' ')
      .replace(/<!--[\s\S]*?-->/g, '')
      .trim()
  } catch (error) {
    console.warn('XML minification error:', error)
    return content
  }
}

function minifyJavaScript(content: string): string {
  try {
    // Basic JS minification - remove comments and unnecessary whitespace
    return content
      .replace(/\/\*[\s\S]*?\*\//g, '') // Remove multi-line comments
      .replace(/\/\/.*$/gm, '') // Remove single-line comments
      .replace(/\s+/g, ' ') // Replace multiple whitespace with single space
      .replace(/;\s*}/g, ';}') // Remove space before closing brace
      .replace(/{\s*/g, '{') // Remove space after opening brace
      .replace(/\s*}/g, '}') // Remove space before closing brace
      .replace(/\s*;\s*/g, ';') // Remove space around semicolons
      .replace(/,\s+/g, ',') // Remove space after commas
      .trim()
  } catch (error) {
    console.warn('JavaScript minification error:', error)
    return content
  }
}

function minifyCss(content: string): string {
  try {
    // Basic CSS minification
    return content
      .replace(/\/\*[\s\S]*?\*\//g, '') // Remove comments
      .replace(/\s+/g, ' ') // Replace multiple whitespace with single space
      .replace(/;\s*}/g, ';}') // Remove space before closing brace
      .replace(/{\s*/g, '{') // Remove space after opening brace
      .replace(/\s*}\s*/g, '}') // Remove space around closing brace
      .replace(/\s*;\s*/g, ';') // Remove space around semicolons
      .replace(/:\s+/g, ':') // Remove space after colons
      .replace(/,\s+/g, ',') // Remove space after commas
      .trim()
  } catch (error) {
    console.warn('CSS minification error:', error)
    return content
  }
}

function minifyHtml(content: string): string {
  try {
    // Basic HTML minification
    return content
      .replace(/<!--[\s\S]*?-->/g, '') // Remove HTML comments
      .replace(/>\s+</g, '><') // Remove whitespace between tags
      .replace(/\s+/g, ' ') // Replace multiple whitespace with single space
      .replace(/\s+>/g, '>') // Remove space before closing tag bracket
      .replace(/>\s+/g, '>') // Remove space after opening tag bracket
      .trim()
  } catch (error) {
    console.warn('HTML minification error:', error)
    return content
  }
}

// Minify document
async function minifyDocument() {
  if (!editor.value) return
  
  try {
    // Use requestAnimationFrame to prevent blocking
    await new Promise(resolve => {
      requestAnimationFrame(async () => {
        try {
          console.groupCollapsed('[MonacoEditor] Minify Document')
          const model = editor.value?.getModel()
          if (!model) {
            console.warn('No model available for minification')
            console.groupEnd()
            resolve(void 0)
            return
          }
          
          const language = model.getLanguageId()
          const content = model.getValue()
          console.log('Language:', language)
          console.log('Original length:', content.length)
          const originalLineCount = content.split(/\n/).length
            
          console.log('Original line count:', originalLineCount)
          
          let minifiedContent: string
          
          switch (language) {
            case 'json':
            case 'jsonc':
              minifiedContent = minifyJson(content)
              break
            case 'xml':
              minifiedContent = minifyXml(content)
              break
            case 'javascript':
            case 'typescript':
              minifiedContent = minifyJavaScript(content)
              break
            case 'css':
            case 'scss':
            case 'less':
              minifiedContent = minifyCss(content)
              break
            case 'html':
              minifiedContent = minifyHtml(content)
              break
            default:
              console.warn(`Minification not supported for language: ${language}`)
              minifiedContent = content.replace(/\s+/g, ' ').trim() // Basic whitespace removal
              break
          }
          
          if (minifiedContent !== content) {
            // Use Monaco's edit operation to integrate with native undo system
            const fullRange = model.getFullModelRange()
            const editOperation: monaco.editor.IIdentifiedSingleEditOperation = {
              range: fullRange,
              text: minifiedContent,
              forceMoveMarkers: true
            }
            
            // Push the edit operation so it integrates with Monaco's undo stack
            model.pushEditOperations([], [editOperation], () => null)
            editorValue.value = minifiedContent
            emit('update:modelValue', minifiedContent)
            const newLineCount = minifiedContent.split(/\n/).length
            console.log('Document minified successfully.')
            console.log('New length:', minifiedContent.length, 'New line count:', newLineCount)
          } else {
            console.log('No changes made during minification (already minimal).')
          }
          console.groupEnd()
          
        } catch (error) {
          console.error('Minify document error:', error)
        }
        resolve(void 0)
      })
    })
  } catch (error) {
    console.error('Minify document failed:', error)
  }
}

// Watch for value changes from parent
watch(() => props.modelValue, (newValue) => {
  if (editor.value && newValue !== editorValue.value) {
    editor.value.setValue(newValue)
    editorValue.value = newValue
  }
})

// Watch for language changes
watch(actualLanguage, (newLanguage) => {
  if (editor.value) {
    monaco.editor.setModelLanguage(editor.value.getModel()!, newLanguage)
  }
})

// Watch for theme changes
watch(() => props.theme, (newTheme) => {
  if (editor.value) {
    const theme = newTheme === 'auto' ? 'vs-dark' : (newTheme === 'hc-light' ? 'vs' : newTheme)
    monaco.editor.setTheme(theme)
    emit('theme-changed', theme)
  }
})

// Watch for height changes and trigger layout update
watch(() => props.height, () => {
  if (editor.value) {
    // Use nextTick to ensure DOM updates are complete
    nextTick(() => {
      editor.value?.layout()
    })
  }
})

// Lifecycle
onMounted(async () => {
  await nextTick()
  await initEditor()
})

onUnmounted(() => {
  // Dispose editor asynchronously to prevent main thread blocking
  if (editor.value) {
    const editorInstance = editor.value
    editor.value = undefined // Clear reference immediately
    
    // Clean up any pending timeouts
    if ((editorInstance as any)._cleanupChangeTimeout) {
      (editorInstance as any)._cleanupChangeTimeout()
    }
    
    // Use multiple async strategies to ensure non-blocking disposal
    setTimeout(() => {
      try {
        // First try to dispose in the next event loop
        editorInstance?.dispose()
      } catch (error) {
        console.warn('Editor disposal error:', error)
        // If disposal fails, try again with requestAnimationFrame
        requestAnimationFrame(() => {
          try {
            editorInstance?.dispose()
          } catch (retryError) {
            console.warn('Editor disposal retry error:', retryError)
          }
        })
      }
    }, 0)
  }
})

// Expose methods
defineExpose({
  formatDocument,
  minifyDocument,
  editor: computed(() => editor.value),
  focus: () => editor.value?.focus(),
  getValue: () => editor.value?.getValue() || '',
  setValue: (value: string) => editor.value?.setValue(value)
})
</script>

<style scoped lang="scss">
.monaco-editor-container {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-color, #ddd);
  border-radius: 4px;
  overflow: hidden;
  background: var(--editor-background, #1e1e1e);
  height: v-bind('editorHeight');
}

.monaco-editor-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--toolbar-background, #2d2d30);
  border-bottom: 1px solid var(--border-color, #3e3e42);
  font-size: 13px;
}

.toolbar-button {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: transparent;
  border: 1px solid var(--border-color, #3e3e42);
  border-radius: 3px;
  color: var(--text-color, #cccccc);
  cursor: pointer;
  font-size: 12px;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background: var(--hover-background, #094771);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.language-info {
  margin-left: auto;
  color: var(--text-muted, #969696);
  font-size: 11px;
  text-transform: uppercase;
}

.monaco-editor {
  flex: 1;
  height: v-bind('editorHeight');
  position: relative;

  &--loading {
    opacity: 0.5;
  }

  &--readonly {
    background: var(--readonly-background, #252526);
  }
}

.monaco-editor-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: var(--text-muted, #969696);
  font-size: 14px;
}
</style>
